import { Link } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Delete from '../../../assets/icons/trash.svg';
import ModalContainer from '../../modalContainer';
import DeleteSchoolModal from '../deleteSchoolModal';
import ProjectLinkModal from '../projectLinkModal';
import {
  cancelSchoolGroup,
  deleteControlSocialActivity,
  deleteControlSocialActivityGroup,
  deleteSchool,
  deleteSchoolGroup
} from '../../../API/apiService';
import {
  buildGroupStatusClassName,
  isGroupCancelled,
  resolveGroupStatus
} from '../../../utils/groupStatus';
import { formatDateInBogota } from '../../../utils/dateTime';

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? '').trim();
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function resolveFormativeProcessType(school) {
  const description = String(
    school?.formativeProcessType ?? getGroupTypeText(school?.groupInfo)
  ).trim();

  if (description) return description;

  const code = String(school?.groupInfo?.code ?? '').trim().toUpperCase();
  if (/GD\d+$/.test(code)) return 'Docentes';
  if (/G\d+$/.test(code)) return 'Estudiantes';

  return '-';
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function SchoolRow({
  school,
  getSchoolsInfo,
  backTo = '',
  isCopScope = false,
  isSocialControlScope = false,
  showFinalizationDate = false
}) {
  const [deleteModal, setDeleteModal] = useState(null);
  const [projectLinkModalOpen, setProjectLinkModalOpen] = useState(false);
  const beneficiariesQuery = new URLSearchParams({
    escuela: school?.code || '',
    group: school?.groupInfo?.code || '',
    groupId: String(school?.groupInfo?.id || '')
  }).toString();

  async function deleteGroup(group, schoolId) {
    const updated = await deleteSchoolGroup(group, schoolId);
    if (updated) {
      setDeleteModal(null);
      toast('Grupo eliminado', {
        type: 'success'
      });
      getSchoolsInfo();
    }
  }

  async function deleteSchoolRecord(schoolId) {
    const updated = await deleteSchool(schoolId);
    if (updated) {
      setDeleteModal(null);
      toast('Entidad aliada eliminada', {
        type: 'success'
      });
      getSchoolsInfo();
    }
  }

  async function cancelGroup(group, schoolId) {
    const updated = await cancelSchoolGroup(group, schoolId);
    if (updated) {
      setDeleteModal(null);
      toast('Grupo cancelado', {
        type: 'success'
      });
      getSchoolsInfo();
    }
  }

  async function deleteSocialControlRecord() {
    const groupId = Number(school?.groupInfo?.id);
    const activityId = Number(school?.id);
    const label = groupId
      ? `el grupo ${school?.groupInfo?.code || ''}`
      : `la actividad ${school?.code || ''}`;

    if (!window.confirm(`¿Estás seguro de que quieres eliminar ${label}? Esta acción no se puede deshacer.`)) {
      return;
    }

    const deleted = groupId
      ? await deleteControlSocialActivityGroup(groupId)
      : await deleteControlSocialActivity(activityId);

    if (deleted) {
      toast(groupId ? 'Grupo eliminado' : 'Actividad eliminada', { type: 'success' });
      getSchoolsInfo();
    }
  }

  const mediatorName =
    getUserDisplayName(school?.groupInfo?.user) || getUserDisplayName(school?.user) || '-';
  const additionalMediators = String(
    school?.additionalMediators ?? school?.additional_mediators ?? ''
  ).trim();
  const municipalityName =
    school?.groupInfo?.municipality?.name ?? school?.municipality?.name ?? '-';
  const neighborhoodName =
    school?.groupInfo?.neighborhood?.name ?? school?.neighborhood?.name ?? '-';
  const organizationName =
    school?.groupInfo?.organization ?? school?.organization ?? '-';
  const canDeleteSchool =
    !(Array.isArray(school?.allGroups) && school.allGroups.length > 0);
  const groupStatus = resolveGroupStatus(school?.groupInfo, '-', {
    schoolCode: school?.code
  });
  const groupCancelled = isGroupCancelled(school?.groupInfo);
  const finalizedAt =
    school?.groupInfo?.finalizedAt ?? school?.groupInfo?.finalized_at ?? '';

  return (
    <>
      {deleteModal && (
        <ModalContainer>
          <DeleteSchoolModal
            mode={deleteModal}
            school={school}
            setDeleteModal={setDeleteModal}
            cancelGroup={cancelGroup}
            deleteGroup={deleteGroup}
            deleteSchool={deleteSchoolRecord}
          />
        </ModalContainer>
      )}
      {projectLinkModalOpen && school?.groupInfo?.id && (
        <ModalContainer>
          <ProjectLinkModal
            group={school.groupInfo}
            onClose={() => setProjectLinkModalOpen(false)}
            onSaved={getSchoolsInfo}
          />
        </ModalContainer>
      )}
      <div className='info-container'>
        <p className='school-option'>{school?.code || '-'}</p>
        <p className='school-option'>{resolveFormativeProcessType(school)}</p>
        {!isCopScope && (
          <p className='school-option'>{school?.groupInfo?.code || '-'}</p>
        )}
        {!isCopScope && (
          <p
            className={`school-option school-option--status school-option--status-${buildGroupStatusClassName(groupStatus)}`}
          >
            {groupStatus}
          </p>
        )}
        {showFinalizationDate && (
          <p className='school-option'>
            {finalizedAt ? formatDateInBogota(finalizedAt) : '-'}
          </p>
        )}
        <p className='school-option'>{mediatorName}</p>
        {isCopScope && (
          <p className='school-option'>{additionalMediators || '-'}</p>
        )}
        <p className='school-option'>{municipalityName}</p>
        <p className='school-option'>{neighborhoodName}</p>
        <p className='school-option'>{organizationName}</p>
        <p className='school-option'>{school?.name || school?.address || '-'}</p>
        <div className='school-option icons-container'>
          {isSocialControlScope ? (
            <button
              onClick={deleteSocialControlRecord}
              className='button-delete'
              type='button'
              title={school?.groupInfo?.id ? 'Eliminar grupo' : 'Eliminar actividad'}
              aria-label={school?.groupInfo?.id ? 'Eliminar grupo' : 'Eliminar actividad'}
            >
              <img src={Delete} alt='' />
            </button>
          ) : null}

          {isSocialControlScope && school?.groupInfo?.id ? (
            <Link
              to={`/escuelas/grupo?activityId=${school?.id}&activityGroupId=${school.groupInfo.id}`}
              state={{
                activity: {
                  idActivity: school?.id,
                  name: school?.code,
                  line: school?.organization
                },
                activityGroup: school.groupInfo,
                backTo
              }}
              className='group'
              title='Visualizar grupo'
              aria-label='Visualizar grupo'
            >
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 29.756 19.837'>
                <path
                  d='M29.576,13.664A16.569,16.569,0,0,0,14.878,4.5,16.572,16.572,0,0,0,.18,13.665a1.671,1.671,0,0,0,0,1.508,16.569,16.569,0,0,0,14.7,9.164,16.572,16.572,0,0,0,14.7-9.165A1.671,1.671,0,0,0,29.576,13.664Zm-14.7,8.193a7.439,7.439,0,1,1,7.439-7.439A7.439,7.439,0,0,1,14.878,21.858Zm0-12.4a4.924,4.924,0,0,0-1.308.2,2.472,2.472,0,0,1-3.456,3.456,4.948,4.948,0,1,0,4.764-3.652Z'
                  transform='translate(0 -4.5)'
                  fill='#00904C'
                />
              </svg>
            </Link>
          ) : null}

          {!isSocialControlScope && school?.groupInfo?.id && !groupCancelled ? (
            <button
              onClick={() => setDeleteModal('cancelGroup')}
              className='button-cancel-group'
              type='button'
              title='Cancelar grupo'
              aria-label='Cancelar grupo'
            >
              <span className='material-symbols-rounded button-cancel-group__icon'>
                block
              </span>
            </button>
          ) : null}

          {!isSocialControlScope && school?.groupInfo?.id ? (
            <button
              onClick={() => setDeleteModal('group')}
              className='button-delete'
              type='button'
            >
              <img src={Delete} alt='' />
            </button>
          ) : null}

          {!isSocialControlScope && canDeleteSchool ? (
            <button
              onClick={() => setDeleteModal('school')}
              className='button-school-delete'
              type='button'
            >
              <span className='button-school-delete__label'>IE</span>
            </button>
          ) : null}

          {!isSocialControlScope && school?.groupInfo?.id ? (
            <button
              type='button'
              className='button-project-link'
              onClick={() => setProjectLinkModalOpen(true)}
              title='Link del proyecto'
              aria-label='Link del proyecto'
            >
              <span className='material-symbols-rounded button-project-link__icon'>
                link
              </span>
            </button>
          ) : null}

          {!isSocialControlScope && school?.groupInfo?.id ? (
            <Link
              to={`/administrador/menu/escuelas/beneficiarios_inscritos?${beneficiariesQuery}`}
              state={backTo ? { backTo } : undefined}
              className='group'
            >
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 29.756 19.837'>
                <path
                  id='Icon_awesome-eye'
                  data-name='Icon awesome-eye'
                  d='M29.576,13.664A16.569,16.569,0,0,0,14.878,4.5,16.572,16.572,0,0,0,.18,13.665a1.671,1.671,0,0,0,0,1.508,16.569,16.569,0,0,0,14.7,9.164,16.572,16.572,0,0,0,14.7-9.165A1.671,1.671,0,0,0,29.576,13.664Zm-14.7,8.193a7.439,7.439,0,1,1,7.439-7.439A7.439,7.439,0,0,1,14.878,21.858Zm0-12.4a4.924,4.924,0,0,0-1.308.2,2.472,2.472,0,0,1-3.456,3.456,4.948,4.948,0,1,0,4.764-3.652Z'
                  transform='translate(0 -4.5)'
                  fill='#00904C'
                />
              </svg>
            </Link>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default SchoolRow;
